import { CVServiceError } from '../../../services/cvService';

/**
 * Utility functions for error handling in CV Builder components
 */

/**
 * Creates a standardized error from various error types
 */
export const createStandardError = (
  error: any,
  defaultMessage: string = 'An unexpected error occurred'
): CVServiceError => {
  if (error instanceof CVServiceError) {
    return error;
  }

  if (error instanceof Error) {
    return new CVServiceError(
      error.message || defaultMessage,
      undefined,
      error
    );
  }

  if (typeof error === 'string') {
    return new CVServiceError(error);
  }

  return new CVServiceError(defaultMessage, undefined, error);
};

/**
 * Determines if an error should trigger a retry mechanism
 */
export const shouldRetry = (error: CVServiceError): boolean => {
  return error.isRetryable;
};

/**
 * Gets user-friendly error message based on error type
 */
export const getUserFriendlyMessage = (error: CVServiceError): string => {
  // Return the error message as it's already user-friendly from CVService
  return error.message;
};

/**
 * Logs error for debugging/monitoring purposes
 */
export const logError = (
  error: CVServiceError,
  context: string,
  additionalData?: any
): void => {
  const errorData = {
    message: error.message,
    statusCode: error.statusCode,
    isRetryable: error.isRetryable,
    context,
    timestamp: new Date().toISOString(),
    ...additionalData
  };

  // Log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.error('CV Builder Error:', errorData, error.originalError);
  }

  // In production, you might want to send to an error reporting service
  // Example: Sentry, LogRocket, etc.
  // errorReportingService.captureException(error, { extra: errorData });
};

/**
 * Error handler for async operations with automatic retry logic
 */
export const withErrorHandling = <T extends any[], R>(
  operation: (...args: T) => Promise<R>,
  context: string,
  onError?: (error: CVServiceError) => void
) => {
  return async (...args: T): Promise<R> => {
    try {
      return await operation(...args);
    } catch (error) {
      const standardError = createStandardError(error);
      logError(standardError, context, { args });
      
      if (onError) {
        onError(standardError);
      }
      
      throw standardError;
    }
  };
};

/**
 * Wraps a component method with error boundary-like behavior
 */
export const withComponentErrorHandling = <T extends any[], R>(
  method: (...args: T) => R,
  fallbackValue: R,
  onError?: (error: Error) => void
) => {
  return (...args: T): R => {
    try {
      return method(...args);
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      
      if (onError) {
        onError(err);
      }
      
      console.error('Component method error:', err);
      return fallbackValue;
    }
  };
};

/**
 * Error recovery strategies
 */
export const ErrorRecoveryStrategies = {
  /**
   * Reload the entire page
   */
  reloadPage: () => {
    window.location.reload();
  },

  /**
   * Navigate to a safe route
   */
  navigateToSafeRoute: (route: string = '/') => {
    window.location.href = route;
  },

  /**
   * Clear local storage and reload
   */
  clearStorageAndReload: () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.reload();
  },

  /**
   * Show a user-friendly error message
   */
  showUserMessage: (message: string) => {
    // This could integrate with a toast notification system
    alert(message);
  }
};